import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { VideoFormatEnum } from '@/app/common/general-types/briefs.dto';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

/** Published / monitored video item shape for inline preview (progressive MP4 only; no HLS). */
export interface VideoPreviewItem {
  cover: string;
  title: string;
  region: string;
  s3_key: string;
  s3_url: string;
  post_id: string;
  musicInfo: {
    album: string;
    music: string;
    title: string;
    author: string;
    duration: number;
    original: boolean;
  };
  s3_bucket: string;
  create_time: number;
  download_count: number;
  noWatermarkMp4: string;
}

@Component({
  selector: 'mb-atom-video-preview',
  imports: [VideoThumbnailComponent],
  templateUrl: './video-preview.html',
  styleUrl: './video-preview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoPreview {
  readonly video = input.required<VideoPreviewItem>();

  private readonly destroyRef = inject(DestroyRef);

  readonly playbackActive = signal(false);
  readonly isPlaying = signal(false);
  readonly currentTimeSec = signal(0);
  readonly durationSec = signal(0);
  readonly isMuted = signal(false);
  readonly isScrubbing = signal(false);

  /** Cover URL resolved; false until preload `load`/`error` when a cover exists. */
  readonly coverImageReady = signal(false);

  readonly showCoverSkeleton = computed(
    () => !!this.thumbnailUrl() && !this.coverImageReady(),
  );

  readonly progressPercent = computed(() => {
    const d = this.durationSec();
    if (!d || d <= 0 || !Number.isFinite(d)) {
      return 0;
    }
    return Math.min(100, (this.currentTimeSec() / d) * 100);
  });

  readonly ariaTimeNow = computed(() => Math.round(this.currentTimeSec()));

  readonly ariaDurationMax = computed(() => {
    const d = this.durationSec();
    return Number.isFinite(d) && d > 0 ? Math.round(d) : 1;
  });

  private readonly videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  private sourcesAttached = false;
  private previousVideoId: string | null = null;

  readonly videoId = computed(
    () => `${this.video().post_id}|${this.video().s3_key}`,
  );

  /** Monitor items omit format; default to vertical (typical social preview). */
  readonly isHorizontal = computed(() => false);

  readonly videoFormatForAtom = computed(
    () => VideoFormatEnum.Vertical,
  );

  readonly thumbnailUrl = computed(() => {
    const u = this.video().cover?.trim();
    return u ? u : undefined;
  });

  readonly thumbnailCdnKey = computed(() => undefined as string | undefined);

  /**
   * Prefer our archived S3 copy: `noWatermarkMp4` is the platform's signed source URL
   * (fbcdn / tikwm) and expires. Fall back to it only if nothing was archived. Never uses HLS.
   */
  readonly playbackUrl = computed(() => {
    const v = this.video();
    const s3 = v.s3_url?.trim();
    if (s3) {
      return s3;
    }
    return v.noWatermarkMp4?.trim() ?? '';
  });

  constructor() {
    effect(() => {
      this.videoId();
      untracked(() => {
        const id = this.videoId();
        if (this.previousVideoId !== null && this.previousVideoId !== id) {
          this.teardownPlayback();
          this.playbackActive.set(false);
          this.sourcesAttached = false;
          this.isPlaying.set(false);
          this.currentTimeSec.set(0);
          this.durationSec.set(0);
          this.isMuted.set(false);
          this.isScrubbing.set(false);
        }
        this.previousVideoId = id;
        if (this.thumbnailUrl()) {
          this.coverImageReady.set(false);
        } else {
          this.coverImageReady.set(true);
        }
      });
    });

    effect(() => {
      const active = this.playbackActive();
      const ref = this.videoPlayer();
      if (active && ref) {
        untracked(() => {
          this.attachSourcesOnce(ref.nativeElement);
        });
      }
    });

    this.destroyRef.onDestroy(() => {
      this.teardownPlayback();
    });
  }

  onPlayClick(): void {
    this.playbackActive.set(true);
  }

  onCoverPreloadDone(): void {
    this.coverImageReady.set(true);
  }

  onControlBarPlayPause(event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.videoPlayer()?.nativeElement;
    if (!ref) {
      return;
    }
    if (ref.paused) {
      void ref.play();
    } else {
      ref.pause();
    }
  }

  onVideoPlay(): void {
    this.isPlaying.set(true);
  }

  onVideoPause(): void {
    this.isPlaying.set(false);
  }

  onTimeUpdate(): void {
    if (this.isScrubbing()) {
      return;
    }
    const el = this.videoPlayer()?.nativeElement;
    if (!el) {
      return;
    }
    this.currentTimeSec.set(el.currentTime);
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      this.durationSec.set(d);
    }
  }

  onLoadedMetadata(): void {
    const el = this.videoPlayer()?.nativeElement;
    if (!el) {
      return;
    }
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      this.durationSec.set(d);
    }
    this.isMuted.set(el.muted);
  }

  onVolumeChange(): void {
    const el = this.videoPlayer()?.nativeElement;
    if (el) {
      this.isMuted.set(el.muted);
    }
  }

  onToggleMute(event: MouseEvent): void {
    event.stopPropagation();
    const el = this.videoPlayer()?.nativeElement;
    if (!el) {
      return;
    }
    el.muted = !el.muted;
    this.isMuted.set(el.muted);
  }

  onProgressKeyDown(event: KeyboardEvent): void {
    const el = this.videoPlayer()?.nativeElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) {
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -5 : 5;
      const next = Math.min(el.duration, Math.max(0, el.currentTime + delta));
      el.currentTime = next;
      this.currentTimeSec.set(next);
    }
  }

  onProgressPointerDown(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const el = this.videoPlayer()?.nativeElement;
    const bar = event.currentTarget as HTMLElement;
    if (!el || !bar) {
      return;
    }
    this.isScrubbing.set(true);
    this.seekFromClientX(event, el, bar);
    const move = (e: MouseEvent | TouchEvent): void => {
      e.preventDefault();
      const v = this.videoPlayer()?.nativeElement;
      if (v) {
        this.seekFromClientX(e, v, bar);
      }
    };
    const stop = (): void => {
      this.isScrubbing.set(false);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', stop);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', stop);
  }

  formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  }

  private seekFromClientX(
    event: MouseEvent | TouchEvent,
    video: HTMLVideoElement,
    bar: HTMLElement,
  ): void {
    const rect = bar.getBoundingClientRect();
    const clientX = this.readClientX(event);
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const dur = video.duration;
    if (!Number.isFinite(dur) || dur <= 0) {
      return;
    }
    const t = pct * dur;
    video.currentTime = t;
    this.currentTimeSec.set(t);
  }

  private readClientX(event: MouseEvent | TouchEvent): number {
    if ('touches' in event && event.touches.length > 0) {
      return event.touches[0].clientX;
    }
    if ('changedTouches' in event && event.changedTouches.length > 0) {
      return event.changedTouches[0].clientX;
    }
    return (event as MouseEvent).clientX;
  }

  private attachSourcesOnce(el: HTMLVideoElement): void {
    if (this.sourcesAttached) {
      return;
    }
    this.sourcesAttached = true;
    const url = this.playbackUrl();
    if (url) {
      el.src = url;
    }
    void el.play().catch(() => {
      /* autoplay policy or decode errors */
    });
  }

  private teardownPlayback(): void {
    const ref = this.videoPlayer()?.nativeElement;
    if (ref) {
      ref.pause();
      ref.removeAttribute('src');
      ref.load();
    }
    this.currentTimeSec.set(0);
    this.durationSec.set(0);
    this.isMuted.set(false);
    this.isScrubbing.set(false);
  }
}
